<template>
    <div class="order-reports-page">
      <div class="container-fluid pt-3">
                <h1>{{ t("orderReports-title") }}</h1>
        <filter-bar
            ref="filterBar"
            @changed="loadData"
            :has-branch-filter="canFilterPerBranch"
            :show-labels="true"
            :filters="extraFilters"
        />
        <div class="clear"></div>
        <loader v-if="is('loading')"></loader>
        <div v-if="is('success')">
            <div class="row stats top-stats">
                <div class="col-sm-12 col-md-4">
                    <div class="box">
                        <div class="clients-info">
                            <div class="icon-info-wrapper">
                                <div class="div-icon">
                                    <Icon icon-class="ui-users-01" size="13" />
                                </div>
                                <div class="info">
                                    <div class="number">
                                        {{ formatNumber(report.counters.clients) }}
                                    </div>
                                    <div class="label">{{ t("orderReports-stats-clients") }}</div>
                                </div>
                            </div>
                            <Segments url="/orders/create-segment" :filters="filters" />
                        </div>
                    </div>
                </div>
                <div class="col-sm-12 col-md-4">
                    <div class="box">
                        <div class="orders-info">
                            <div class="icon-info-wrapper">
                                <div class="div-icon">
                                    <Icon icon-class="ui-shopping-cart-03" size="13" />
                                </div>
                                <div class="info">
                                    <div>
                                        <div class="number">
                                            {{ formatNumber(report.counters.orders) }}
                                        </div>
                                        <div class="label">{{ t("orderReports-stats-orders") }}</div>
                                    </div>
                                </div>
                            </div>
                            <button
                                v-if="hasPermission('automations')"
                                class="orders-download"
                                @click.prevent="downloadOfflineEvents()"
                                :aria-label="t('orderReports-downloads-orders_aria_label')"
                            >
                                <Icon
                                    icon-class="ui-download-01"
                                    size="6"
                                    :tooltip="t('orderReports-downloads-orders_tooltip')"
                                />
                            </button>
                        </div>
                    </div>
                </div>
                <div class="col-sm-12 col-md-4">
                    <div class="box">
                        <div class="icon-info-wrapper">
                            <div class="div-icon">
                                <Icon icon-class="ui-bank-note-01" size="13" />
                            </div>
                            <div class="info">
                                <div class="number">
                                    {{ formatCurrency(report.totalIncome) }}
                                </div>
                                <div class="label">{{ t("orderReports-stats-total_income") }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row stats bottom-stats">
                <div class="col-sm-12 col-md-4">
                    <div class="box">
                        <div class="icon-info-wrapper">
                            <div class="div-icon">
                                <Icon icon-class="ui-coins-stacked-03" size="13" />
                            </div>
                            <div class="info">
                                <div class="number">
                                    {{ formatCurrency(report.avgTicket) }}
                                </div>
                                <div class="label">{{ t("orderReports-stats-avg_ticket") }}</div>
                            </div>
                        </div>
                    </div>
                </div>
                <div
                    class="col-sm-12 col-md-4"
                    v-if="report.periodInDays >= 7 && report.avgTimeBetweenOrders !== null"
                >
                    <div class="box">
                        <div class="icon-info-wrapper">
                            <div class="div-icon">
                                <Icon icon-class="ui-calendar-clock" size="13" />
                            </div>
                            <div class="info">
                                <div class="number">
                                    {{
                                        formatNumber(
                                            report.avgTimeBetweenOrders,
                                        )
                                    }}
                                    {{ t("orderReports-stats-days_suffix") }}
                                </div>
                                <div class="label">
                                    {{ t("orderReports-stats-avg_time_between_orders") }}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-sm-12 col-md-4">
                    <div class="box">
                        <div class="receipt-info">
                            <div class="icon-info-wrapper">
                                <div class="div-icon">
                                    <Icon icon-class="ui-bank-note-01" size="13" />
                                </div>
                                <div class="info">
                                    <div class="number">
                                        {{ formatCurrency(report.incentivizedIncome) }}
                                    </div>
                                    <div class="label">
                                        {{ t("orderReports-stats-incentivized_income") }}
                                        <span v-if="report.totalIncome">
                                            ({{
                                                (
                                                    (report.incentivizedIncome /
                                                        report.totalIncome) *
                                                    100
                                                ).toFixed(2)
                                            }}%)
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <button
                                class="orders-download"
                                @click.prevent="downloadIncentivizedOrders()"
                                :aria-label="t('orderReports-downloads-incentivized_orders_aria_label')"
                            >
                                <Icon
                                    icon-class="ui-download-01"
                                    size="6"
                                    :tooltip="t('orderReports-downloads-incentivized_orders_tooltip')"
                                />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row main-chart">
                <div class="col-sm-12">
                    <orders-income
                        :height="218"
                        v-if="report.periodInDays > 1"
                    />
                    <weekly-orders v-if="report.periodInDays == 1" />
                </div>
            </div>
            <div class="row charts-row">
                <div class="col-sm-3">
                    <div class="row stats">
                        <div
                            class="col-xs-12 col-sm-12"
                        >
                            <div class="box chart-holder pie-chart">
                                <h2>
                                    {{
                                        t("orderReports-stats-base_clients", [
                                            100 - report.newClientsPercentage,
                                        ])
                                    }}
                                </h2>
                                <pie-chart :chart-data="pieCustomerChartData" :height="200" />
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-sm-9">
                    <div class="row">
                        <div class="col-xs-12 col-sm-12">
                            <orders-per-client v-if="report.counters.clients > 0" />
                        </div>
                    </div>
                </div>
            </div>
            <div class="row" v-if="report.periodInDays > 1">
                <div class="col-sm-12">
                    <weekly-orders />
                </div>
            </div>
            <div class="fields-chart">
                <income-by-field
                    v-for="(field, label) in report.ordersPerField"
                    :key="label"
                    :field-data="field"
                    :label="label"
                />
            </div>
        </div>
        <div v-if="is('empty')">
            <span
                class="alert alert-info"
                style="margin-bottom: 10px; display: inline-block"
            >
                {{ t("orderReports-states-empty") }}
            </span>
        </div>
      </div>
    </div>
</template>
<style lang="less" scoped>
.orders-download {
    color: #000;
    border: none;
    background-color: transparent;
}

.icon-info-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
}

.info {
    display: flex;
    flex-direction: column;
    text-align: left;
    gap: 4px;
}

.orders-info,
.clients-info,
.receipt-info {
    display: flex;
    align-items: center;
    justify-content: space-between;

    & > button {
        padding: 0 !important;
    }
}

.page-content.orders-report {
    max-width: 100%;

    .fields-chart {
        display: flex;
        justify-content: stretch;
        flex-wrap: wrap;
        gap: 0 20px;
        .income-by-field {
            flex-grow: 1;
        }
    }
}

.div-icon {
    color: var(--secondary);
}

.box.chart-holder {
    height: 284px;
}

@media (max-width: 1200px) {
    .orders-info,
    .clients-info,
    .receipt-info {
        align-items: flex-start;
    }

    .icon-info-wrapper {
        flex-direction: column;
        align-items: flex-start;
    }
}

@media (hover: none) and (pointer: coarse) {
    .page-content.orders-report {
        .fields-chart {
            display: block;
        }
    }
}
</style>
<script>
import DatetimeWithClear from "../components/datetime-with-clear/datetime-with-clear.vue";
import saveData from "../helpers/saveData";
import OrdersIncome from "./charts/orders-income";
import WeeklyOrders from "./charts/weekly-orders";
import ordersPerClient from "./charts/orders-per-client";
import IncomeByField from "./charts/income-by-field";
import FilterBar from "../components/reports/filter-bar";
import keviApi from "../keviApi";
import Vue from "vue";
import Segments from "../components/segments/segments";
import { mapState } from "vuex";
import Icon from "../components/icon/icon";
import currencyMixin from "../mixins/currency-mixin";
require("./order-report.store.js");

export default {
    mixins: [currencyMixin],
    components: {
        FilterBar,
        WeeklyOrders,
        OrdersIncome,
        ordersPerClient,
        IncomeByField,
        DatetimeWithClear,
        Segments,
        Icon
    },
    computed: {
        ...mapState("placeDetails", ["users", "roles", "branches"]),
        pieCustomerChartData() {
            return {
                labels: [
                    this.t("orderReports-pie_chart-first_time"),
                    this.t("orderReports-pie_chart-returned"),
                ],
                datasets: [
                    {
                        data: [
                            this.report.counters.newClients,
                            this.report.counters.clients -
                                this.report.counters.newClients,
                        ],
                        backgroundColor: ["#A7CCC0", "#00542E"],
                    },
                ],
            };
        },
        status() {
            return this.$store.state.ordersReport.status;
        },
        user() {
            return this.$store.state.user;
        },
        placeId() {
            return this.$store.state.place.id;
        },
        report() {
            return this.$store.state.ordersReport.report;
        },
        canFilterPerBranch() {
            return !!this.user.permissions["reports"]["options"][
                "global_access"
            ];
        },
        hasBranchFilter() {
            let filters = this.$store.state.ordersReport.filters;
            if (
                Object.keys(filters).includes("branch") &&
                Array.isArray(filters.branch)
            ) {
                return filters.branch.length;
            }
            return false;
        },
    },
    data: () => ({
        filters: {},
        extraFilters: [],
    }),
    mounted() {
        document.getElementsByTagName("title")[0].innerText =
                        this.t("orderReports-page_title");
        const filterBar = this.$refs.filterBar;
        filterBar.addFilter("period");
        this.extraFilters.push(
          {
            component: "period",
            label: this.t("orderReports-filters-first_order_period"),
            name: "first-order-period",
            removable: true,
          }
        );
        if (this.$module("segments")) {
            let options = keviApi.getOptions("segments");
            this.$http(options).then((response) => {
                let segments = response.body;
                this.extraFilters.push({
                    component: "checkbox",
                    label: this.t("orderReports-filters-client_segments"),
                    name: "client-segments",
                    options: {
                        options: segments.map((seg) => ({
                            value: seg.id,
                            label: seg.name,
                        })),
                        icon: "fa-tag",
                    },
                    removable: true,
                });
            });
        }
        if (this.$module("order_status")) {
            let options = keviApi.getOptions(
                "settings/external-order-status",
                "GET",
            );
            this.$http(options).then((response) => {
                if (response.status === 200) {
                    let status = response.body.status;
                    this.extraFilters.push({
                        component: "checkbox",
                        label: this.t("orderReports-filters-order_status"),
                        name: "order-status",
                        options: {
                            options: status.map((s) => ({
                                value: s.id,
                                label: s.external_label,
                            })),
                            icon: "fa-tag",
                        },
                        removable: true,
                    });
                }
            });
        }
        let fieldOptions = keviApi.getOptions("/order-fields", "GET", {
            "only-report-fields": 1,
        });
        this.$http(fieldOptions).then((response) => {
            for (let f in response.body) {
                let field = response.body[f];
                if (field.type === "user") {
                    this.$store
                        .dispatch("placeDetails/setPlace", this.placeId)
                        .then(() => {
                            let users = this.users.filter(
                                (u) => parseInt(u.active) === 1,
                            );
                            this.extraFilters.push({
                                component: "selectbox",
                                label: field.field_name,
                                name: "f_" + field.field_identifier,
                                options: [
                                    ...users.map((u) => ({
                                        value: u.id,
                                        label: u.name,
                                    })),
                                ],
                                removable: true,
                            });
                        });
                } else {
                    this.extraFilters.push({
                        component: "textfield",
                        label: field.field_name,
                        name: "f_" + field.field_identifier,
                        options: {
                            options: [],
                            icon: "fa-tag",
                        },
                        removable: true,
                    });
                }
            }
        });
    },

    methods: {
        is(status) {
            return this.status === status;
        },
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        async loadData(filters) {
            this.filters = filters;
            await this.$store.dispatch("ordersReport/loadData", filters);
            window.mixpanel?.track("Order Report", {
                report: this.report,
                filters: this.filters,
            });
        },
        formatNumber(number) {
            return parseFloat(number).toLocaleString("pt-BR");
        },
        downloadOfflineEvents() {
            let data = {
                filters: this.filters,
            };
            let requestOptions = keviApi.getOptions(
                "orders/export-to-facebook",
                "GET",
                data,
            );
            Vue.http(requestOptions).then((response) => {
                if (response.status === 200) {
                    if (response.body) {
                        window.mixpanel?.track(
                            "Order Report download facebook offline events",
                        );
                        saveData(response.body, "offline-events.csv");
                    }
                }
            });
        },
        downloadIncentivizedOrders() {
            let data = {
                filters: this.filters,
            };
            let requestOptions = keviApi.getOptions(
                "orders/export-incentivized",
                "GET",
                data,
            );
            Vue.http(requestOptions).then((response) => {
                if (response.status === 200) {
                    if (response.body) {
                        window.mixpanel?.track(
                            "Order Report download facebook offline events",
                        );
                        saveData(response.body, "pedidos-incentivados.csv");
                    }
                }
            });
        },
        hasPermission(permission) {
            if (this.user) {
                return this.user.permissions[permission].enabled;
            } else {
                return false;
            }
        },
    },
};
</script>
